import dayjs from "dayjs";
import { ChipOptionsDataType } from "../../components";
import { BRACKETS, SYMBOL } from "../../helpers/constants";
import {
  formatSeconds,
  getFromToTimeDuration,
  getFullName,
  getValueOrBlank,
  isEmpty,
} from "../../helpers/utils";
import {
  CoachDetails,
  MY_MEETINGS_PAST_PERIOD_FILTER_KEYS,
  MY_MEETINGS_TYPE_FILTER_KEYS,
  MY_MEETINGS_UPCOMING_PERIOD_FILTER_KEYS,
  PAST_PERIOD_FILTER_KEYS,
  TYPE_FILTER_KEYS,
  UPCOMING_PERIOD_FILTER_KEYS,
} from "./modelsAndSchemas/Models";

export const getCoachNamesWithType = (
  coaches: CoachDetails[],
  loggedInCoachId: string,
) => {
  if (isEmpty(coaches) || isEmpty(loggedInCoachId)) return [];

  const formatCoach = (coach: CoachDetails) => {
    const coachType = coach.coachTypes?.map((t) => t.name).join(", ") ?? "";
    const fullName = getFullName(coach.firstName, coach.lastName, true);
    return {
      label: fullName,
      value: getValueOrBlank(coachType),
    };
  };

  // place loggedInCoach first, then all others
  return [
    ...coaches.filter((c) => c.id === loggedInCoachId).map(formatCoach),
    ...coaches.filter((c) => c.id !== loggedInCoachId).map(formatCoach),
  ];
};

export const getInfoMessageForAssignedCoaches = (
  coaches: ChipOptionsDataType[],
) => {
  return `${coaches[0]?.label?.toString()} ${BRACKETS.LEFT_SQUARE}${coaches[0]?.value?.toString()}${BRACKETS.RIGHT_SQUARE}`;
};

// 🔹 Helpers
const isSameDay = (target: dayjs.Dayjs, reference: dayjs.Dayjs) => {
  return target.isSame(reference, "day");
};

const isWithinPastDays = (
  target: dayjs.Dayjs,
  now: dayjs.Dayjs,
  days: number,
) => {
  return target.isBefore(now) && target.isAfter(now.subtract(days, "day"));
};

const isWithinNextDays = (
  target: dayjs.Dayjs,
  now: dayjs.Dayjs,
  days: number,
) => {
  return target.isAfter(now) && target.isBefore(now.add(days, "day"));
};

const isWithinNextMonths = (
  target: dayjs.Dayjs,
  now: dayjs.Dayjs,
  months: number,
) => {
  return target.isAfter(now) && target.isBefore(now.add(months, "month"));
};

const myMeetingsPastChecks: Record<
  string,
  (target: dayjs.Dayjs, now: dayjs.Dayjs) => boolean
> = {
  [MY_MEETINGS_PAST_PERIOD_FILTER_KEYS.TODAY]: (target, now) =>
    isSameDay(target, now),

  [MY_MEETINGS_PAST_PERIOD_FILTER_KEYS.YESTERDAY]: (target, now) =>
    isSameDay(target, now.subtract(1, "day")),

  [MY_MEETINGS_PAST_PERIOD_FILTER_KEYS.PAST_7_DAYS]: (target, now) =>
    isWithinPastDays(target, now, 7),

  [MY_MEETINGS_PAST_PERIOD_FILTER_KEYS.PAST_30_DAYS]: (target, now) =>
    isWithinPastDays(target, now, 30),

  [MY_MEETINGS_PAST_PERIOD_FILTER_KEYS.PAST_3_MONTHS]: (target, now) =>
    isWithinPastDays(target, now, 90),
};

const myMeetingsUpcomingChecks: Record<
  string,
  (target: dayjs.Dayjs, now: dayjs.Dayjs) => boolean
> = {
  [MY_MEETINGS_UPCOMING_PERIOD_FILTER_KEYS.TODAY]: (target, now) =>
    isSameDay(target, now),

  [MY_MEETINGS_UPCOMING_PERIOD_FILTER_KEYS.TOMORROW]: (target, now) =>
    isSameDay(target, now.add(1, "day")),

  [MY_MEETINGS_UPCOMING_PERIOD_FILTER_KEYS.NEXT_7_DAYS]: (target, now) =>
    isWithinNextDays(target, now, 7),

  [MY_MEETINGS_UPCOMING_PERIOD_FILTER_KEYS.NEXT_30_DAYS]: (target, now) =>
    isWithinNextDays(target, now, 30),

  [MY_MEETINGS_UPCOMING_PERIOD_FILTER_KEYS.NEXT_3_MONTHS]: (target, now) =>
    isWithinNextMonths(target, now, 3),
};

const sessionsPastChecks: Record<
  string,
  (target: dayjs.Dayjs, now: dayjs.Dayjs) => boolean
> = {
  [PAST_PERIOD_FILTER_KEYS.PAST_7_DAYS]: (target, now) =>
    isWithinPastDays(target, now, 7),

  [PAST_PERIOD_FILTER_KEYS.PAST_14_DAYS]: (target, now) =>
    isWithinPastDays(target, now, 14),

  [PAST_PERIOD_FILTER_KEYS.PAST_1_MONTH]: (target, now) =>
    isWithinPastDays(target, now, 30),

  [PAST_PERIOD_FILTER_KEYS.PAST_3_MONTHS]: (target, now) =>
    isWithinPastDays(target, now, 90),

  [PAST_PERIOD_FILTER_KEYS.PAST_1_YEAR]: (target, now) =>
    isWithinPastDays(target, now, 365),

  [PAST_PERIOD_FILTER_KEYS.PAST_3_YEARS]: (target, now) =>
    isWithinPastDays(target, now, 1095),
};

const sessionsUpcomingChecks: Record<
  string,
  (target: dayjs.Dayjs, now: dayjs.Dayjs) => boolean
> = {
  [UPCOMING_PERIOD_FILTER_KEYS.NEXT_7_DAYS]: (target, now) =>
    isWithinNextDays(target, now, 7),

  [UPCOMING_PERIOD_FILTER_KEYS.NEXT_14_DAYS]: (target, now) =>
    isWithinNextDays(target, now, 14),

  [UPCOMING_PERIOD_FILTER_KEYS.NEXT_1_MONTH]: (target, now) =>
    isWithinNextDays(target, now, 30),

  [UPCOMING_PERIOD_FILTER_KEYS.NEXT_3_MONTHS]: (target, now) =>
    isWithinNextMonths(target, now, 3),
};

// utility to check if date falls in selected period
const periodChecks = {
  ...myMeetingsPastChecks,
  ...myMeetingsUpcomingChecks,
  ...sessionsPastChecks,
  ...sessionsUpcomingChecks,
};

const typeToChecks: Record<string, typeof periodChecks> = {
  [MY_MEETINGS_TYPE_FILTER_KEYS.PAST_SESSION]: myMeetingsPastChecks,
  [MY_MEETINGS_TYPE_FILTER_KEYS.UPCOMING_SESSION]: myMeetingsUpcomingChecks,
  [TYPE_FILTER_KEYS.PAST_SESSIONS_AND_EVENTS]: sessionsPastChecks,
  [TYPE_FILTER_KEYS.UPCOMING_SESSIONS_AND_EVENTS]: sessionsUpcomingChecks,
};

export const isWithinPeriod = (
  date: string,
  period: string,
  type: string,
): boolean => {
  if (isEmpty(date) || isEmpty(period)) return true;

  const now = dayjs().utc();
  const target = dayjs(date).utc();

  // 🔹 Directly check against unified map
  const checks = typeToChecks[type];
  return checks?.[period]?.(target, now) ?? true;
};

export const getFormattedDuration = (
  startTime: string,
  endTime: string,
  durationInSeconds: number,
  timeZone?: string,
) => {
  const formattedTimeDurationInHour = formatSeconds(durationInSeconds);
  const parts = [
    getFromToTimeDuration(startTime, endTime, timeZone),
    getValueOrBlank(formattedTimeDurationInHour),
  ].filter(Boolean);

  if (isEmpty(parts)) return "";

  const joinedResult = [parts.join(` ${SYMBOL.PIPE} `)]
    .filter(Boolean)
    .join(" ");
  return joinedResult;
};
